/**
 * The always-on rule form's "IN SHORT" box (Prototype: Reach → Automatic →
 * New rule): the rule in one sentence as it's built — "When an article
 * mentioning MLB is published, your agent posts it to X and Threads as soon as
 * it can." Kept apart from the form so tests can load it.
 */

export interface SentenceInput {
  include: string[];
  exclude: string[];
  /** Media kinds the article must be: bookmark | photo | video. */
  mediaTypes: string[];
  /** Channel names, in the order shown. */
  channels: string[];
  mode: 'asap' | 'times' | 'interval' | 'marks';
  times: string[];
  intervalMinutes: number;
  marks: number[];
  delayHours: number;
  /** 0 = Sunday. */
  days: number[];
  start: string;
  end: string;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const KIND: Record<string, string> = { bookmark: 'link', photo: 'photo', video: 'video' };

/** "a", "a and b", "a, b and c". */
export const joinWords = (a: string[], last = 'and') =>
  a.length <= 1 ? a[0] || '' : `${a.slice(0, -1).join(', ')} ${last} ${a[a.length - 1]}`;

/** "09:00" → "9 AM", "13:30" → "1:30 PM". */
export function to12(v: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(v || '').trim());
  if (!m) return v;
  const h = Number(m[1]);
  const min = m[2] === '00' ? '' : `:${m[2]}`;
  return `${h % 12 || 12}${min} ${h < 12 ? 'AM' : 'PM'}`;
}

/** How often within the hours: "on the quarter hour", "every 2 hours", "at :05 past the hour". */
export function cadenceWords(
  s: Pick<SentenceInput, 'mode' | 'times' | 'intervalMinutes' | 'marks'>
) {
  if (s.mode === 'times' && s.times.length) return `at ${joinWords(s.times.map(to12))}`;
  const marks =
    s.mode === 'interval'
      ? 60 % s.intervalMinutes === 0
        ? Array.from({ length: 60 / s.intervalMinutes }, (_, i) => i * s.intervalMinutes)
        : null
      : s.mode === 'marks' && s.marks.length
        ? [...new Set(s.marks)].sort((a, b) => a - b)
        : null;
  const key = marks?.join();
  if (key === '0') return 'on the hour';
  if (key === '0,30') return 'on the hour and half hour';
  if (key === '0,15,30,45') return 'on the quarter hour';
  if (s.mode === 'interval') {
    const n = s.intervalMinutes;
    return n % 60 === 0 ? `every ${n / 60} hours` : `every ${n} minutes`;
  }
  if (marks)
    return `at ${joinWords(marks.map((m) => `:${String(m).padStart(2, '0')}`))} past the hour`;
  return 'as soon as it can';
}

/** "every day", "on weekdays", "on weekends", "on Mon, Wed and Fri"; '' for any day. */
export function dayWords(days: number[]): string {
  const ds = [...new Set(days)].sort((a, b) => a - b);
  const key = ds.join();
  if (!ds.length) return '';
  if (key === '0,1,2,3,4,5,6') return 'every day';
  if (key === '1,2,3,4,5') return 'on weekdays';
  if (key === '0,6') return 'on weekends';
  return `on ${joinWords(ds.map((d) => DAYS[d]))}`;
}

/**
 * When it posts: "every day between 9 AM and 5 PM, on the quarter hour",
 * "2 hours after it’s published, then at 9 AM", "as soon as it can".
 */
export function whenWords(s: SentenceInput): string {
  let w = cadenceWords(s);
  if (s.delayHours > 0) {
    w = `${s.delayHours} ${s.delayHours === 1 ? 'hour' : 'hours'} after it’s published, then ${w}`;
  }
  const d = dayWords(s.days);
  return d ? `${d} between ${to12(s.start)} and ${to12(s.end)}, ${w}` : w;
}

/** The rule detail's "Only on" row: "Every day · 9 AM–5 PM", "Mon and Wed · 9 AM–5 PM". */
export function onlyOnWords(days: number[], spans: Array<[string, string]>): string {
  const d = dayWords(days).replace(/^on /, '');
  const hours = [...new Set(spans.map(([a, b]) => `${to12(a)}–${to12(b)}`))].join(' or ');
  return `${d.charAt(0).toUpperCase()}${d.slice(1)} · ${hours}`;
}

/** A migrated rule's older filter, as the server puts it in words (conditionsForApp). */
export interface LegacyFilter {
  include: string[];
  exclude: string[];
  readable: boolean;
}

/**
 * The older filter in plain words, and what removing it changes. It's a
 * substring match in the title, address, keywords or category (the old rules
 * screen); topics match whole words anywhere, text and tags included, and both
 * apply — so removing it only ever lets more articles through.
 */
export function legacyFilterWords(f: LegacyFilter, topics: string[]): string {
  const q = (w: string) => `“${w}”`;
  const lower = new Set(topics.map((t) => t.toLowerCase()));
  if (!f.readable) {
    return (
      'A filter from before rules had topics also limits which articles post. It can’t be ' +
      'shown or edited here; removing it lets in every article the rows above allow.'
    );
  }
  const what = [
    f.include.length
      ? `Only articles with ${joinWords(f.include.map(q), 'or')} in the title, address, keywords or category`
      : '',
    f.exclude.length
      ? `${f.include.length ? ', and none' : 'No articles'} with ${joinWords(f.exclude.map(q), 'or')} there`
      : '',
  ].join('');
  const covered = f.include.length > 0 && f.include.every((w) => lower.has(w.toLowerCase()));
  const after = covered
    ? `Your topics already look for ${joinWords(f.include, 'and')}; removing it also lets in articles that mention ${f.include.length === 1 ? 'it' : 'them'} only in their text or tags.`
    : 'Removing it lets in every article the rows above allow.';
  return `${what}. Set up before rules had topics, so it can’t be edited. ${after}`;
}

export function alwaysOnSentence(s: SentenceInput): string {
  const kinds = s.mediaTypes.length
    ? ` Only ${joinWords(
        s.mediaTypes.map((k) => `${KIND[k] || k} articles`),
        'or'
      )}.`
    : '';
  return (
    'When an article' +
    (s.include.length ? ` mentioning ${joinWords(s.include, 'or')}` : '') +
    (s.exclude.length ? `, but not ${joinWords(s.exclude, 'or')},` : '') +
    ` is published, your agent posts it to ${joinWords(s.channels) || 'no channels yet'} ` +
    `${whenWords(s)}.${kinds}`
  );
}

/** A saved rule's conditions (AlwaysOnConditions), read for the sentence. */
export interface SavedConditions {
  include?: string[];
  exclude?: string[];
  mediaTypes?: string[];
  delayHours?: number;
  schedule?: Array<{ time?: string; minuteMark?: number; intervalMinutes?: number }>;
  windows?: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
}

/** The rule detail's sentence (Prototype: under the rule's title), from what's saved. */
export function sentenceFromConditions(
  k: SavedConditions | null | undefined,
  channels: string[]
): string {
  const c = k || {};
  const schedule = c.schedule || [];
  let mode: SentenceInput['mode'] = 'asap';
  if (schedule.some((e) => e.time)) mode = 'times';
  else if (schedule.length === 1 && (schedule[0].minuteMark ?? 0) === 0) mode = 'interval';
  else if (schedule.length) mode = 'marks';
  const windows = c.windows || [];
  return alwaysOnSentence({
    include: c.include || [],
    exclude: c.exclude || [],
    mediaTypes: (c.mediaTypes || []).map((m) => m.toLowerCase()),
    channels,
    mode,
    times: schedule.map((e) => e.time).filter(Boolean) as string[],
    intervalMinutes: schedule[0]?.intervalMinutes || 60,
    marks: schedule.map((e) => Number(e.minuteMark) || 0),
    delayHours: c.delayHours || 0,
    days: [...new Set(windows.map((w) => w.dayOfWeek))],
    start: windows[0]?.startTime?.slice(0, 5) || '09:00',
    end: windows[0]?.endTime?.slice(0, 5) || '17:00',
  });
}
